import React, { type ReactElement, type ComponentProps, useMemo } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import { ChevronDown } from "../icons";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";
import { useFocusRing } from "../util/useFocusRing";

export interface SelectProps {
  /**
   * When the button is pressed
   */
  onPress: () => void;
  /**
   * Disables the control.
   */
  disabled: boolean;
  /**
   * Styles the input to indicate that it is invalid.
   */
  invalid: boolean;
  /**
   * Label to show in the button for the currently selected element
   */
  label?: string;
  /**
   * Placeholder to show when no element is selected
   */
  placeholder?: string;
  /**
   * When true, the label is constrained to a single line and ellipsizes so a
   * long value cannot push the chevron out of the field. Defaults to false to
   * preserve the existing layout for all other consumers. See RN-9687.
   */
  truncateLabel?: boolean;
  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;
}

/**
 * Implements a button to display for the Select component
 */
export function SelectButton({
  ref,
  onPress,
  invalid,
  disabled,
  label,
  placeholder,
  truncateLabel = false,
  sx,
  ...a11yProps
}: SelectProps & ComponentProps<typeof Pressable>): ReactElement {
  const theme = useTheme();
  const { isFocusVisible, focusProps } = useFocusRing();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        base: {
          flexDirection: "row",
          zIndex: 0,
          backgroundColor: theme.colors.background,
          borderRadius: theme.borderRadius.md,
          borderWidth: 1,
        },
        buttonContainer: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginVertical: theme.spacing.s3,
          marginHorizontal: theme.spacing.s3,
          flex: 1,
        },
        // Applied only when `truncateLabel` is set. Lets the label shrink and
        // ellipsize so a long value (e.g. a long month name at large text
        // sizes) does not push the chevron out of the field. See RN-9687.
        label: {
          flexShrink: 1,
        },
        // Keeps the chevron at its intrinsic size and always visible next to
        // the (possibly truncated) label.
        chevron: {
          flexShrink: 0,
          marginLeft: theme.spacing.s2,
        },
        buttonStyles: {
          zIndex: 1,
          borderWidth: 0,
        },
      }),
    [theme],
  );

  const labelColor = label ? theme.colors.foregroundStrong : theme.colors.foregroundWeak;

  return (
    // accessibilityRole and other SelectInput props are set via a11yProps for ARIA flexibility
    // eslint-disable-next-line @indeed/indeed-a11y-react-native/has-accessibility-role
    <Pressable
      ref={ref}
      testID="select-button"
      style={[
        styles.base,
        {
          borderColor: invalid ? theme.colors.foregroundStatusCritical : theme.colors.borderAction,
        },
        isFocusVisible && {
          outlineStyle: "solid",
          outlineWidth: 2,
          outlineColor: theme.colors.utilityFocus,
          outlineOffset: 2,
        },
        sx,
      ]}
      onPress={onPress}
      disabled={disabled}
      {...a11yProps}
      onFocus={focusProps.onFocus}
      onBlur={focusProps.onBlur}
    >
      <View aria-invalid={invalid} style={styles.buttonContainer}>
        <Text
          level={3}
          numberOfLines={truncateLabel ? 1 : undefined}
          sx={truncateLabel ? [styles.label, { color: labelColor }] : { color: labelColor }}
        >
          {label || placeholder}
        </Text>
        {truncateLabel ? (
          <View testID="select-chevron" style={styles.chevron}>
            <ChevronDown aria-hidden={true} color={theme.colors.foregroundStrong} />
          </View>
        ) : (
          <ChevronDown aria-hidden={true} color={theme.colors.foregroundStrong} />
        )}
      </View>
    </Pressable>
  );
}
